import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("hgs_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const me = await api.auth.me();
      setUser(me);
    } catch (e) {
      localStorage.removeItem("hgs_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await api.auth.login(email, password);
    localStorage.setItem("hgs_token", res.token);
    setUser(res.user);
    qc.invalidateQueries();
    return res.user;
  }, [qc]);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } catch (e) { /* ignore */ }
    localStorage.removeItem("hgs_token");
    setUser(null);
    qc.clear();
  }, [qc]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
